import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DatabaseOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Database } from 'lucide-react';

import { Bento, PageHeader, StatTile, Tile, TileBody, TabsTruot } from '../../../components/shared';
import { Button, Empty, Skeleton } from '../../../components/ui';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { KHONG_CO, soVN } from '../../../lib/dinh-dang';
import { KhongCoQuyen, LoiRpa } from '../components/chung';
import { BangDuLieu } from '../components/du-lieu/BangDuLieu';
import { TabDoiChieu } from '../components/du-lieu/TabDoiChieu';
import { TabKetQuaCao } from '../components/du-lieu/TabKetQuaCao';
import { BadgeTrangThaiLuot } from '../components/lich-su/nhan';
import { QUYEN, TINH_TRANG_SO_DONG } from '../constants';
import { useThongKeDuLieu } from '../hooks/useLichSu';
import { KHOA_RPA } from '../hooks/useRpa';
import { kiemSoDong, type TinhTrangSoDong } from '../nghiep-vu/du-lieu';
import type { KhoaLuotDuLieu } from '../types';

const DUONG_LICH_SU = '/rpa/history';

const ICON_TINH_TRANG: Record<TinhTrangSoDong, ReactNode> = {
  match: <CheckCircleOutlined aria-hidden />,
  missing: <CloseCircleOutlined aria-hidden />,
  extra: <ExclamationCircleOutlined aria-hidden />,
  empty: <InfoCircleOutlined aria-hidden />,
};

/**
 * Dữ liệu thực thi của một lượt chạy (`/rpa/history/execution-data?instanceId=&name=&status=`, `RpaExecutionDataView`
 * của native), mở từ Lịch sử luồng chạy: số dòng cào / đã xử lý, tình trạng đối chiếu, hai bảng dữ liệu đầy đủ, dòng
 * thiếu / dư và kết quả cào dạng thẻ. Không có mục điều hướng riêng — trang chi tiết.
 *
 * Lượt không có InstanceId thì tra theo `runId` (mã Robot gửi), như máy chủ nhận.
 */
export function DuLieuThucThiView() {
  const { t, locale } = useT();
  const qc = useQueryClient();
  const dieuHuong = useDieuHuong();
  const quyen = useModulePermissions(QUYEN);
  const [thamSo] = useSearchParams();
  const instanceId = thamSo.get('instanceId')?.trim() ?? '';
  const runId = thamSo.get('runId')?.trim() ?? '';
  const status = thamSo.get('status') ?? '';
  const khoa: KhoaLuotDuLieu = instanceId ? { instanceId } : runId ? { runId } : {};
  const maLuot = instanceId || runId;
  const executionId = thamSo.get('executionId')?.trim() || maLuot;
  const tk = useThongKeDuLieu(quyen.lichSuDoc ? khoa : {});
  const luong = thamSo.get('name') || tk.data?.workflowName || '';
  const tieuDe = t('rpaDuLieu.tieuDe');

  return (
    <div className="space-y-4 p-4 lg:p-6">
      <PageHeader
        breadcrumb={[
          { label: t('rpa.nhom') },
          { label: t('nav.muc.quanLyProjectWorkflow') },
          { label: t('nav.muc.lichSuLuongChay') },
          { label: tieuDe },
        ]}
        titleIcon={<Database size={18} className="text-primary-600" aria-hidden />}
        title={tieuDe}
        subtitle={t('rpaDuLieu.phuDe', { luong: luong || t('rpaDuLieu.chuaRoLuong'), ma: maLuot || KHONG_CO })}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="md" icon={<ArrowLeftOutlined />} onClick={() => dieuHuong(DUONG_LICH_SU)}>
              {t('rpaDuLieu.quayLai')}
            </Button>
            {quyen.lichSuDoc && maLuot && (
              <Button
                variant="outline"
                size="md"
                icon={<ReloadOutlined />}
                loading={tk.isFetching && !tk.isPending}
                onClick={() => void qc.invalidateQueries({ queryKey: KHOA_RPA.duLieu(maLuot) })}
              >
                {t('chung.lamMoi')}
              </Button>
            )}
          </div>
        }
      />

      {!quyen.lichSuDoc ? (
        <KhongCoQuyen quyen={QUYEN.lichSuDoc} />
      ) : !maLuot ? (
        <Tile>
          <Empty icon={DatabaseOutlined} title={t('rpaDuLieu.khongCoInstance')} />
        </Tile>
      ) : tk.isPending ? (
        <div className="space-y-4" aria-busy="true">
          <Skeleton className="h-24 w-full" />
          <Tile>
            <Skeleton className="h-64 w-full" />
          </Tile>
        </div>
      ) : tk.isError ? (
        <Tile>
          <LoiRpa loi={tk.error} onThuLai={() => void tk.refetch()} />
        </Tile>
      ) : (
        <>
          <Bento>
            <StatTile
              span="sm:col-span-6 lg:col-span-3"
              nhan={t('rpaDuLieu.soCao')}
              so={soVN(tk.data.crawled, locale)}
              sub={t('rpaDuLieu.dong')}
              icon={<DatabaseOutlined />}
              tone="primary"
            />
            <StatTile
              span="sm:col-span-6 lg:col-span-3"
              nhan={t('rpaDuLieu.soXuLy')}
              so={soVN(tk.data.processed, locale)}
              sub={t('rpaDuLieu.dong')}
              icon={<CheckCircleOutlined />}
              tone="teal"
            />
            <div className="col-span-12 flex min-w-0 flex-col justify-center gap-2 lg:col-span-6">
              {status && (
                <span className="self-start">
                  <BadgeTrangThaiLuot status={status} />
                </span>
              )}
              <TinhTrang cao={tk.data.crawled} xuLy={tk.data.processed} />
            </div>
          </Bento>

          <Tile className="min-w-0">
            <TileBody>
              <TabsTruot
                defaultActiveKey="crawled"
                items={[
                  {
                    key: 'crawled',
                    label: t('rpaDuLieu.tab.crawled'),
                    children: <BangDuLieu khoa={khoa} loai="crawled" luong={luong} maLuot={maLuot} />,
                  },
                  {
                    key: 'processed',
                    label: t('rpaDuLieu.tab.processed'),
                    children: <BangDuLieu khoa={khoa} loai="processed" luong={luong} maLuot={maLuot} />,
                  },
                  { key: 'compare', label: t('rpaDuLieu.tab.compare'), children: <TabDoiChieu khoa={khoa} luong={luong} maLuot={maLuot} /> },
                  { key: 'scrape', label: t('rpaDuLieu.tab.scrape'), children: <TabKetQuaCao executionId={executionId} /> },
                ]}
              />
            </TileBody>
          </Tile>
        </>
      )}
    </div>
  );
}

/** Xử lý đủ số đã cào là khớp; thiếu là lỗi (`role=alert`); nhiều hơn là dư. */
function TinhTrang({ cao, xuLy }: { cao: number; xuLy: number }) {
  const { t, locale } = useT();
  const { tinhTrang, lech } = kiemSoDong(cao, xuLy);
  const so = { lech: soVN(lech, locale), cao: soVN(cao, locale), xuLy: soVN(xuLy, locale) };
  return (
    <p
      role={tinhTrang === 'missing' ? 'alert' : 'status'}
      className={`flex items-start gap-2 rounded-lg border px-4 py-3 text-sm ${TINH_TRANG_SO_DONG[tinhTrang]}`}
    >
      <span className="mt-0.5">{ICON_TINH_TRANG[tinhTrang]}</span>
      <span>
        <b>{t(`rpaDuLieu.tinhTrang.${tinhTrang}`)}</b>
        {tinhTrang === 'match' && ` · ${t('rpaDuLieu.chiTiet.match', so)}`}
        {tinhTrang === 'missing' && ` · ${t('rpaDuLieu.chiTiet.missing', so)}`}
        {tinhTrang === 'extra' && ` · ${t('rpaDuLieu.chiTiet.extra', so)}`}
      </span>
    </p>
  );
}
